import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Clock, User, Share2, Linkedin, Twitter, Facebook, CheckCircle, TrendingUp, Users, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useBlogContactPopup } from "@/hooks/useBlogContactPopup";
import DemoScheduler from "@/components/DemoScheduler";
import eleevaLogo from "@/assets/logos/EleevaIT-AltaDefinicao.png";

const CaseStudyEleevaIT = () => {
    const { isOpen, setIsOpen } = useBlogContactPopup(3000);

    const shareUrl = "https://aroan.com.br/case-study/eleeva-it";
    const shareTitle = "Case de Sucesso: Como a ELEEVA IT Reduziu em 80% o Tempo de Atendimento com AROAN";
    const shareDescription = "Descubra como a ELEEVA IT transformou sua operação de suporte com o AROAN, alcançando 80% de redução no tempo de atendimento e 85% de aumento na velocidade de resolução.";

    const handleLinkedInShare = () => {
        const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        window.open(linkedInUrl, '_blank', 'width=600,height=400');
    };

    const handleTwitterShare = () => {
        const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
        window.open(twitterUrl, '_blank', 'width=600,height=400');
    };

    const handleFacebookShare = () => {
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        window.open(facebookUrl, '_blank', 'width=600,height=400');
    };

    return (
        <div className="min-h-screen bg-background">
            <SEO
                title="Case Eleeva IT: Tempo de Atendimento de 5h para 1h"
                description="Como a Eleeva IT reduziu em 80% o tempo de atendimento com a Aroan: tempo médio de 5h para 1h, 85% de resolução no primeiro contato e satisfação 9,5/10."
                canonical="https://aroan.com.br/case-study/eleeva-it"
                publishedTime="2024-12-01T00:00:00-03:00"
            />
            <BreadcrumbSchema
                items={[
                    { name: "Home", url: "https://aroan.com.br/" },
                    { name: "Case Eleeva IT", url: "https://aroan.com.br/case-study/eleeva-it" },
                ]}
            />
            <Header />

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 bg-gradient-hero overflow-hidden">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <Badge className="mb-6 bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                            Case de Sucesso
                        </Badge>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8 leading-tight animate-fade-in">
                            Como a <span className="text-primary">ELEEVA IT</span> Reduziu em 80% o Tempo de Atendimento
                        </h1>

                        <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground animate-fade-in-delay-1 mb-8">
                            <div className="flex items-center gap-2">
                                <User className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">Equipe AROAN</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">Dezembro, 2024</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Clock className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">8 min de leitura</span>
                            </div>
                        </div>

                        {/* Company Logo */}
                        <div className="flex justify-center mb-8">
                            <div className="bg-white p-6 rounded-2xl shadow-lg">
                                <img
                                    src={eleevaLogo}
                                    alt="ELEEVA IT Logo"
                                    className="h-16 w-auto object-contain"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Key Metrics Section */}
            <section className="py-12 bg-muted/30">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid md:grid-cols-3 gap-8">
                            <div className="text-center p-6 bg-card rounded-2xl border border-border">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <TrendingUp className="w-8 h-8 text-green-600" />
                                </div>
                                <h3 className="text-3xl font-bold text-green-600 mb-2">80%</h3>
                                <p className="text-muted-foreground">Redução no Tempo de Atendimento</p>
                            </div>
                            <div className="text-center p-6 bg-card rounded-2xl border border-border">
                                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Zap className="w-8 h-8 text-blue-600" />
                                </div>
                                <h3 className="text-3xl font-bold text-blue-600 mb-2">70%</h3>
                                <p className="text-muted-foreground">Redução no Trabalho Manual</p>
                            </div>
                            <div className="text-center p-6 bg-card rounded-2xl border border-border">
                                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Users className="w-8 h-8 text-purple-600" />
                                </div>
                                <h3 className="text-3xl font-bold text-purple-600 mb-2">85%</h3>
                                <p className="text-muted-foreground">Aumento na Velocidade de Resolução</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="py-12 bg-background">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl mx-auto">
                        
                        {/* Introduction */}
                        <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
                            <h2 className="text-3xl font-bold mb-6 text-foreground">Introdução</h2>
                            <p className="text-lg leading-relaxed">
                                A ELEEVA IT, uma empresa de tecnologia em rápido crescimento, enfrentava desafios significativos em sua operação de suporte ao cliente. Com sistemas desconectados, processos manuais e falta de visibilidade, a equipe de suporte lutava para atender às crescentes demandas dos clientes, resultando em longos tempos de espera e baixa satisfação. A empresa precisava de uma solução que pudesse unificar suas operações, automatizar tarefas e fornecer a inteligência necessária para tomar decisões estratégicas. A implementação do AROAN, um sistema integrado de gestão inteligente, foi a resposta para esses desafios.
                            </p>
                        </div>

                        {/* The Challenge */}
                        <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
                            <h2 className="text-3xl font-bold mb-6 text-foreground">O Desafio</h2>
                            <p>
                                Antes do AROAN, a ELEEVA IT operava em um ambiente fragmentado, com vários sistemas que não se comunicavam. Isso criava silos de informação, dificultando a colaboração entre as equipes e o rastreamento de problemas. Os principais desafios incluíam:
                            </p>

                            <div className="space-y-6 my-8">
                                <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg">
                                    <h3 className="text-lg font-bold mb-3 text-red-800">Processos Manuais e Lentos</h3>
                                    <p className="mb-0 text-red-700">
                                        A criação, o roteamento e a resolução de tickets eram feitos manualmente, resultando em atrasos e erros.
                                    </p>
                                </div>

                                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg">
                                    <h3 className="text-lg font-bold mb-3 text-yellow-800">Falta de Visibilidade</h3>
                                    <p className="mb-0 text-yellow-700">
                                        A gestão não tinha uma visão clara do desempenho da equipe de suporte, dos principais problemas dos clientes ou do status dos ativos de TI.
                                    </p>
                                </div>

                                <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
                                    <h3 className="text-lg font-bold mb-3 text-blue-800">Base de Conhecimento Inexistente</h3>
                                    <p className="mb-0 text-blue-700">
                                        A falta de uma base de conhecimento centralizada levava a um trabalho repetitivo, com a equipe de suporte resolvendo os mesmos problemas várias vezes.
                                    </p>
                                </div>

                                <div className="bg-purple-50 border-l-4 border-purple-400 p-6 rounded-r-lg">
                                    <h3 className="text-lg font-bold mb-3 text-purple-800">Dificuldade no Rastreamento de Ativos</h3>
                                    <p className="mb-0 text-purple-700">
                                        A empresa não conseguia rastrear seus ativos de TI de forma eficaz, o que dificultava a análise de impacto em caso de incidentes.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* The Solution */}
                        <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
                            <h2 className="text-3xl font-bold mb-6 text-foreground">A Solução: Implementação do AROAN</h2>
                            <p>
                                A ELEEVA IT implementou o AROAN para unificar e automatizar suas operações de suporte. A plataforma integrou a gestão de tickets, ativos, checklists e uma base de conhecimento inteligente, fornecendo uma solução completa para os desafios da empresa. Os principais módulos e recursos utilizados foram:
                            </p>

                            <div className="space-y-6 my-8">
                                <div className="bg-green-50 border-l-4 border-green-400 p-6 rounded-r-lg">
                                    <h3 className="text-lg font-bold mb-3 text-green-800 flex items-center gap-2">
                                        <CheckCircle className="w-5 h-5" />
                                        Gestão de Ativos
                                    </h3>
                                    <p className="mb-0 text-green-700">
                                        O AROAN forneceu uma visão do inventário completo e atualizado de todos os ativos de TI, com mapeamento de relacionamentos. Isso permitiu que a equipe de suporte entendesse o impacto de cada ativo nos serviços de negócio e resolvesse incidentes com mais rapidez.
                                    </p>
                                </div>

                                <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
                                    <h3 className="text-lg font-bold mb-3 text-blue-800 flex items-center gap-2">
                                        <CheckCircle className="w-5 h-5" />
                                        Workflows Automáticos Inteligentes
                                    </h3>
                                    <p className="mb-0 text-blue-700">
                                        A empresa automatizou o roteamento de tickets, as aprovações e a criação de tarefas com os workflows do AROAN. Isso eliminou o trabalho manual, reduziu o tempo de resposta e garantiu que os problemas fossem direcionados para as equipes certas.
                                    </p>
                                </div>

                                <div className="bg-purple-50 border-l-4 border-purple-400 p-6 rounded-r-lg">
                                    <h3 className="text-lg font-bold mb-3 text-purple-800 flex items-center gap-2">
                                        <CheckCircle className="w-5 h-5" />
                                        Base de Conhecimento Inteligente
                                    </h3>
                                    <p className="mb-0 text-purple-700">
                                        A ELEEVA IT criou uma base de conhecimento centralizada com o AROAN, permitindo que a equipe de suporte encontrasse soluções para problemas conhecidos rapidamente. A integração com o portal de autoatendimento também permitiu que os clientes resolvessem seus próprios problemas, reduzindo o número de tickets.
                                    </p>
                                </div>

                                <div className="bg-indigo-50 border-l-4 border-indigo-400 p-6 rounded-r-lg">
                                    <h3 className="text-lg font-bold mb-3 text-indigo-800 flex items-center gap-2">
                                        <CheckCircle className="w-5 h-5" />
                                        Formulários Inteligentes
                                    </h3>
                                    <p className="mb-0 text-indigo-700">
                                        A empresa utilizou os formulários inteligentes do AROAN para coletar informações de forma estruturada e acionar processos automatizados. Isso simplificou a abertura de chamados e garantiu que a equipe de suporte tivesse todas as informações necessárias para resolver os problemas.
                                    </p>
                                </div>

                                <div className="bg-teal-50 border-l-4 border-teal-400 p-6 rounded-r-lg">
                                    <h3 className="text-lg font-bold mb-3 text-teal-800 flex items-center gap-2">
                                        <CheckCircle className="w-5 h-5" />
                                        Dashboards e Relatórios Inteligentes
                                    </h3>
                                    <p className="mb-0 text-teal-700">
                                        Com os dashboards do AROAN, a gestão da ELEEVA IT passou a ter visibilidade em tempo real de métricas críticas, como tempo de resposta, tempo de resolução e satisfação do cliente. Isso permitiu que a empresa identificasse gargalos e tomasse decisões baseadas em dados para melhorar continuamente seus processos.
                                    </p>
                                </div>

                                <div className="bg-orange-50 border-l-4 border-orange-400 p-6 rounded-r-lg">
                                    <h3 className="text-lg font-bold mb-3 text-orange-800 flex items-center gap-2">
                                        <CheckCircle className="w-5 h-5" />
                                        VIA - O Assistente de IA
                                    </h3>
                                    <p className="mb-0 text-orange-700">
                                        O assistente de IA do AROAN, VIA, forneceu sugestões inteligentes durante a resolução de tickets, analisou o conteúdo dos chamados e automatizou tarefas repetitivas, aumentando a produtividade da equipe de suporte.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Results */}
                        <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
                            <h2 className="text-3xl font-bold mb-6 text-foreground">Os Resultados</h2>
                            <p>
                                A implementação do AROAN transformou completamente a operação de suporte da ELEEVA IT. A empresa alcançou resultados impressionantes, incluindo:
                            </p>

                            <div className="space-y-6 my-8">
                                <div className="bg-green-50 border-l-4 border-green-400 p-6 rounded-r-lg">
                                    <h3 className="text-lg font-bold mb-3 text-green-800">Redução de 80% no Tempo de Atendimento</h3>
                                    <p className="mb-0 text-green-700">
                                        A automação de processos, a base de conhecimento inteligente e o assistente de IA permitiram que a equipe de suporte resolvesse os problemas dos clientes com muito mais rapidez.
                                    </p>
                                </div>

                                <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
                                    <h3 className="text-lg font-bold mb-3 text-blue-800">Redução de 70% no Trabalho Manual</h3>
                                    <p className="mb-0 text-blue-700">
                                        A automação de tarefas repetitivas liberou a equipe de suporte para se concentrar em atividades de maior valor.
                                    </p>
                                </div>

                                <div className="bg-purple-50 border-l-4 border-purple-400 p-6 rounded-r-lg">
                                    <h3 className="text-lg font-bold mb-3 text-purple-800">Aumento de 85% na Velocidade de Resolução</h3>
                                    <p className="mb-0 text-purple-700">
                                        A combinação de workflows inteligentes e acesso rápido à informação acelerou a resolução de problemas.
                                    </p>
                                </div>
                            </div>

                            {/* Results Table */}
                            <div className="overflow-x-auto my-8">
                                <table className="w-full border-collapse border border-border rounded-lg">
                                    <thead>
                                        <tr className="bg-muted">
                                            <th className="border border-border p-4 text-left font-bold">Métrica</th>
                                            <th className="border border-border p-4 text-center font-bold">Antes do AROAN</th>
                                            <th className="border border-border p-4 text-center font-bold">Depois do AROAN</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="border border-border p-4"><strong>Tempo médio de atendimento</strong></td>
                                            <td className="border border-border p-4 text-center">5 horas</td>
                                            <td className="border border-border p-4 text-center font-bold text-green-600">1 hora</td>
                                        </tr>
                                        <tr className="bg-muted/50">
                                            <td className="border border-border p-4"><strong>Resolução no primeiro contato</strong></td>
                                            <td className="border border-border p-4 text-center">40%</td>
                                            <td className="border border-border p-4 text-center font-bold text-green-600">85%</td>
                                        </tr>
                                        <tr>
                                            <td className="border border-border p-4"><strong>Satisfação do cliente</strong></td>
                                            <td className="border border-border p-4 text-center">7.5/10</td>
                                            <td className="border border-border p-4 text-center font-bold text-green-600">9.5/10</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Conclusion */}
                        <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
                            <h2 className="text-3xl font-bold mb-6 text-foreground">Conclusão</h2>
                            <p>
                                A implementação do AROAN foi um divisor de águas para a ELEEVA IT. A plataforma não apenas resolveu os desafios operacionais da empresa, mas também forneceu a base para uma cultura de melhoria contínua. Com o AROAN, a ELEEVA IT transformou sua operação de suporte de um centro de custos em um diferencial competitivo, aumentando a satisfação do cliente e impulsionando o crescimento do negócio.
                            </p>
                        </div>

                        {/* CTA Section */}
                        <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-8 rounded-2xl text-center mt-16">
                            <h3 className="text-2xl font-bold mb-4">Transforme Sua Operação Como a ELEEVA IT</h3>
                            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                                Descubra como o AROAN pode revolucionar sua gestão de suporte e alcançar resultados similares aos da ELEEVA IT.
                            </p>
                            <Button size="lg" className="group" onClick={() => setIsOpen(true)}>
                                Agendar Demonstração
                                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </Button>
                        </div>

                        {/* Share Section */}
                        <div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
                            <div>
                                <h4 className="font-semibold mb-2">Compartilhe este case</h4>
                                <div className="flex gap-3">
                                    <Button size="sm" variant="outline" className="group" onClick={handleLinkedInShare}>
                                        <Linkedin className="w-4 h-4 mr-2 group-hover:text-primary transition-colors" />
                                        LinkedIn
                                    </Button>
                                    <Button size="sm" variant="outline" className="group" onClick={handleTwitterShare}>
                                        <Twitter className="w-4 h-4 mr-2 group-hover:text-primary transition-colors" />
                                        Twitter
                                    </Button>
                                    <Button size="sm" variant="outline" className="group" onClick={handleFacebookShare}>
                                        <Facebook className="w-4 h-4 mr-2 group-hover:text-primary transition-colors" />
                                        Facebook
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
            <DemoScheduler open={isOpen} onOpenChange={setIsOpen} />
        </div>
    );
};

export default CaseStudyEleevaIT;
